import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  VStack,
  Flex,
  useDisclosure,
  Text,
  Grid,
  GridItem,
  useColorModeValue,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  ButtonGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon, SearchIcon, DownloadIcon, RepeatIcon } from '@chakra-ui/icons';
import { FaUsers, FaPrint, FaRegFilePdf, FaRegFileExcel, FaTree, FaBuilding, FaUserCheck, FaUserPlus, FaExclamationTriangle } from 'react-icons/fa';
import { Icon } from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAccountabilityChart } from './useAccountabilityChart';
import { RoleCard } from './components/RoleCard';
import { EditRoleModal } from './components/EditRoleModal';
import { RoleActionsModal } from './components/RoleActionsModal';
import { DepartmentsView } from './components/DepartmentsView';
import { ConfirmModal } from '../Rocks/components/ConfirmModal';
import { exportAccountabilityChartCSV, exportAccountabilityChartPDF } from './utils';
import { RoleNode } from './components/RoleNode';
import { Role } from '../types';

export const AccountabilityChartPage = () => {
  const { currentUser, SuperAdmin } = useAuth();
  const {
    roles,
    loading,
    error,
    saveRole,
    deleteRole,
    getRolesByLevel,
    getChildRoles,
  } = useAccountabilityChart();

  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [addingChildTo, setAddingChildTo] = useState<Role | null>(null);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'departments'>('tree');

  const visionaryRef = useRef<HTMLDivElement>(null);
  const integratorRef = useRef<HTMLDivElement>(null);
  const departmentsContainerRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Set document title (matching legacy code)
  useEffect(() => {
    document.title = 'Accountability Chart - EOS';
  }, []);

  const visionary = getRolesByLevel(0)[0];
  const integrator = getRolesByLevel(1)[0];
  const departments = getRolesByLevel(2);

  const handleSaveRole = async (values: Partial<Role>) => {
    if (!currentUser) return;

    try {
      const roleId = editingRole?.id;
      await saveRole(values, roleId, addingChildTo || undefined);
      setShowEditModal(false);
      setEditingRole(null);
      setAddingChildTo(null);
      setErrorMessage(null);
    } catch (err: any) {
      console.error('Error saving role:', err);
      setErrorMessage(err.message || 'Failed to save role.');
    }
  };

  const handleDeleteRole = async () => {
    if (!deletingRole) return;

    try {
      await deleteRole(deletingRole.id);
      setDeletingRole(null);
      setErrorMessage(null);
    } catch (err: any) {
      console.error('Error deleting role:', err);
      setErrorMessage(err.message || 'Failed to delete role.');
    }
  };

  // All hooks must be called before any early returns
  const borderColor = useColorModeValue('gray.900', 'gray.100');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.700');

  if (loading) {
    return (
      <Box p={8}>
        <Flex align="center" justify="center" h="64">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.600" thickness="4px" />
            <Text color="gray.600">Loading accountability chart...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  const totalSeats = roles.length;
  const filledSeats = roles.filter(r => !!r.ownerName).length;
  const vacantSeats = totalSeats - filledSeats;

  return (
    <Box p={8}>
      <Box mb={6}>
        <Flex align="flex-start" justify="space-between" flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" mb={1} display="flex" alignItems="center" gap={3}>
              <Icon as={FaUsers} w={7} h={7} color="blue.600" />
              Accountability Chart
            </Heading>
            <Text color="gray.500" fontSize="sm">
              Define roles, responsibilities, and organizational structure
            </Text>
          </Box>
          <Flex align="center" gap={3} flexWrap="wrap">
            <InputGroup w="64" size="sm" bg="white">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input type="text" placeholder="Search seats, people, or" borderRadius="md" />
            </InputGroup>
            
            <Button size="sm" variant="ghost" leftIcon={<Icon as={FaRegFileExcel} />} onClick={() => exportAccountabilityChartCSV(roles)}>
              Export CSV
            </Button>
            <Button size="sm" variant="ghost" leftIcon={<Icon as={FaRegFilePdf} />} onClick={() => exportAccountabilityChartPDF(chartContainerRef)}>
              Export PDF
            </Button>
            <Button size="sm" variant="ghost" leftIcon={<Icon as={FaPrint} />} onClick={() => window.print()}>
              Quick Print
            </Button>

            <ButtonGroup size="sm" isAttached variant="outline" bg="white">
              <Button 
                leftIcon={<Icon as={FaTree} />} 
                onClick={() => setViewMode('tree')}
                isActive={viewMode === 'tree'}
                bg={viewMode === 'tree' ? 'gray.100' : 'transparent'}
              >
                Tree
              </Button>
              <Button 
                leftIcon={<Icon as={FaBuilding} />} 
                onClick={() => setViewMode('departments')}
                isActive={viewMode === 'departments'}
                bg={viewMode === 'departments' ? 'gray.100' : 'transparent'}
              >
                Departments
              </Button>
            </ButtonGroup>

            <IconButton
              aria-label="Refresh"
              icon={<RepeatIcon />}
              size="sm"
              variant="outline"
              bg="white"
            />
            {SuperAdmin && (
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<AddIcon />}
                onClick={() => {
                  setAddingChildTo(null);
                  setShowEditModal(true);
                }}
              >
                Add Role
              </Button>
            )}
          </Flex>
        </Flex>
      </Box>

      {/* Stats Row */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={8}>
        <Box bg="white" p={4} borderRadius="lg" borderWidth="1px" borderColor="gray.100" shadow="sm">
          <Flex align="center" gap={4}>
            <Box p={3} bg="gray.50" borderRadius="md" color="gray.600">
              <Icon as={FaUsers} w={5} h={5} />
            </Box>
            <Box>
              <Text fontSize="2xl" fontWeight="bold" lineHeight="1">{totalSeats}</Text>
              <Text fontSize="xs" color="gray.500" mt={1}>Total Seats</Text>
            </Box>
          </Flex>
        </Box>
        <Box bg="white" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.100" shadow="sm" position="relative" overflow="hidden">
           <Box position="absolute" top="0" left="0" w="4px" h="full" bg="green.400" />
          <Flex align="center" gap={4}>
            <Box p={3} bg="green.50" borderRadius="md" color="green.600">
              <Icon as={FaUserCheck} w={5} h={5} />
            </Box>
            <Box>
              <Text fontSize="2xl" fontWeight="bold" lineHeight="1">{filledSeats}</Text>
              <Text fontSize="xs" color="gray.500" mt={1}>Filled</Text>
            </Box>
          </Flex>
        </Box>
        <Box bg="white" p={4} borderRadius="lg" borderWidth="1px" borderColor="orange.100" shadow="sm" position="relative" overflow="hidden">
           <Box position="absolute" top="0" left="0" w="4px" h="full" bg="orange.200" />
          <Flex align="center" gap={4}>
            <Box p={3} bg="orange.50" borderRadius="md" color="orange.500">
              <Icon as={FaUserPlus} w={5} h={5} />
            </Box>
            <Box>
              <Text fontSize="2xl" fontWeight="bold" lineHeight="1">{vacantSeats}</Text>
              <Text fontSize="xs" color="gray.500" mt={1}>Vacant</Text>
            </Box>
          </Flex>
        </Box>
        <Box bg="white" p={4} borderRadius="lg" borderWidth="1px" borderColor="gray.100" shadow="sm">
          <Flex align="center" gap={4}>
            <Box p={3} bg="gray.100" borderRadius="md" color="gray.500">
              <Icon as={FaExclamationTriangle} w={5} h={5} />
            </Box>
            <Box>
              <Text fontSize="2xl" fontWeight="bold" lineHeight="1">0</Text>
              <Text fontSize="xs" color="gray.500" mt={1}>GWC Issues</Text>
            </Box>
          </Flex>
        </Box>
      </Grid>

      {(error || errorMessage) && (
        <Alert status="error" mb={6} borderRadius="lg">
          <AlertIcon />
          <AlertDescription flex="1">
            <Text fontWeight="medium" mb={1}>
              Error
            </Text>
            <Text fontSize="sm">{error || errorMessage}</Text>
          </AlertDescription>
          <IconButton
            aria-label="Close error"
            icon={<CloseIcon />}
            size="sm"
            variant="ghost"
            onClick={() => {
              setErrorMessage(null);
            }}
          />
        </Alert>
      )}

      <Box
        ref={chartContainerRef}
        bg={cardBg}
        borderRadius="lg"
        shadow="sm"
        borderWidth="1px"
        borderColor={cardBorderColor}
        p={8}
        overflowX="auto"
        className="tree-container"
      >
        <Box position="relative" minH="600px" w="fit-content" mx="auto" minW="full">
          {viewMode === 'tree' ? (
            visionary && (
              <Flex justify="center" minW="max-content">
                <RoleNode
                  role={visionary}
                  allRoles={roles}
                  onRoleClick={(r) => {
                    setSelectedRole(r);
                    setShowActionsModal(true);
                  }}
                  onAddChildClick={(parentRole) => {
                    setAddingChildTo(parentRole);
                    setShowEditModal(true);
                  }}
                  isSuperUser={SuperAdmin}
                  defaultExpanded={true}
                />
              </Flex>
            )
          ) : (
            <Box w="full" mx="auto">
              <DepartmentsView
                departments={departments}
                allRoles={roles}
                onRoleClick={(r) => {
                  setSelectedRole(r);
                  setShowActionsModal(true);
                }}
                onAddChildClick={(parentRole) => {
                  setAddingChildTo(parentRole);
                  setShowEditModal(true);
                }}
                isSuperUser={SuperAdmin}
              />
            </Box>
          )}

          {!visionary && (
            <Box textAlign="center" py={12}>
              <Icon as={FaUsers} w={16} h={16} color="gray.300" mx="auto" mb={4} display="block" />
              <Text color="gray.600" mb={4}>
                No roles defined yet
              </Text>
              {SuperAdmin && (
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  onClick={() => {
                    setAddingChildTo(null);
                    setShowEditModal(true);
                  }}
                >
                  Create First Role
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Role Actions Modal */}
      <RoleActionsModal
        isOpen={showActionsModal}
        onClose={() => {
          setShowActionsModal(false);
          setSelectedRole(null);
        }}
        onEdit={() => {
          if (selectedRole) {
            setEditingRole(selectedRole);
            setShowEditModal(true);
            setShowActionsModal(false);
          }
        }}
        onDelete={() => {
          if (selectedRole) {
            setDeletingRole(selectedRole);
            setShowActionsModal(false);
          }
        }}
        onAddChild={
          selectedRole
            ? () => {
                setAddingChildTo(selectedRole);
                setShowEditModal(true);
                setShowActionsModal(false);
              }
            : undefined
        }
        roleTitle={selectedRole?.title || ''}
      />

      {/* Edit Role Modal */}
      <EditRoleModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingRole(null);
          setAddingChildTo(null);
        }}
        onSave={handleSaveRole}
        role={editingRole || undefined}
        title={editingRole ? 'Edit Role' : 'Add New Role'}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deletingRole}
        onClose={() => setDeletingRole(null)}
        onConfirm={handleDeleteRole}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${deletingRole?.title}"? This will also delete all roles below it.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};
